import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2 } from "lucide-react";

/**
 * ImageUploader — shared control for uploading an image (or PDF) to the
 * SBT object-storage backend and returning a public URL that gets stored
 * on the record being edited.
 *
 * Props:
 *   - value:      current URL (string) — full or relative path.
 *   - onChange:   (url: string) => void  ← called after successful upload or paste.
 *   - accept:     MIME hint, default 'image/*'.
 *   - category:   optional tag (e.g. 'hero-slider', 'partner-logo') stored in DB.
 *   - preview:    'square' | 'wide' | 'none'  (default 'square' 88px thumb).
 *   - label:      accessible label for the input.
 *   - testId:     data-testid prefix.
 */
export default function ImageUploader({
  value = "",
  onChange,
  accept = "image/*",
  category = "",
  preview = "square",
  label = "Image",
  testId = "image-uploader",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const resolvedSrc = value
    ? (value.startsWith("http") || value.startsWith("data:") ? value : `${API.replace(/\/api$/, "")}${value}`)
    : "";

  const doUpload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (category) form.append("category", category);
    setBusy(true);
    try {
      const r = await api.post("/admin/sbt-site/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data.url || r.data.public_url;
      onChange?.(url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `${label} upload failed`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const thumbClass =
    preview === "wide"
      ? "w-40 h-20 rounded border bg-slate-50 flex items-center justify-center overflow-hidden"
      : preview === "none"
      ? ""
      : "w-16 h-16 rounded border bg-slate-50 flex items-center justify-center overflow-hidden";

  return (
    <div className="flex items-center gap-3">
      {preview !== "none" && (
        <div className={thumbClass} data-testid={`${testId}-thumb`}>
          {resolvedSrc ? (
            <img src={resolvedSrc} alt={label} className="max-w-full max-h-full object-contain"/>
          ) : (
            <span className="text-[10px] text-slate-400 text-center px-1">no image</span>
          )}
        </div>
      )}

      <div className="flex-1 space-y-1">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Paste an https:// URL or upload →"
          className="text-xs h-8"
          data-testid={`${testId}-url`}
        />
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
            data-testid={`${testId}-file`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-btn`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Upload className="w-3.5 h-3.5 mr-1"/>}
            {busy ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
}
